import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { HostApiService, ProductService } from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, Observer, firstValueFrom, from, of as observableOf } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { EnvironmentConfig, IConfiguration } from '../../../client-config/bma-env-config';
import { WindowRefService } from '../windowRef/window-ref.service';

@Injectable({
    providedIn: 'root',
})
export class AsyncScriptLoaderService {
    private loadedFiles: Map<string, boolean> = new Map();
    isProxiedEnv = false;

    private loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private loadingStarted: boolean = false;
    private data: { [key: string]: string };

    targatedElm: HTMLLinkElement;
    manifestData: { [key: string]: string } = {};
    private readonly environment: IConfiguration = inject(EnvironmentConfig).configuration;
    vanillaApiService = inject(HostApiService);
    productService = inject(ProductService);
    constructor(
        private windowRef: WindowRefService,
        private http: HttpClient,
    ) {
        this.isProxiedEnv = this.isHashIncluded('main.js');
    }

    getCssAssetFileName(fileName: string) {
        return from(this.getEntry(fileName));
    }

    loadJsFile(fileName: string, attrs?: { [key: string]: string }, idName?: string): Observable<string | null> {
        if (this.loadedFiles.has(fileName) && this.loadedFiles.get(fileName)) {
            return observableOf(null);
        }
        return Observable.create((observer: Observer<string>) => {
            const script = this.windowRef.document.createElement('script');
            script.type = 'text/javascript';
            if (idName) {
                script.id = idName;
            }

            if (attrs) {
                const attributes = Object.keys(attrs);
                attributes.forEach((attribute: string) => {
                    script.setAttribute(attribute, attrs[attribute]);
                });
            }

            script.src = fileName;
            script.onload = () => this.onLoad(observer, fileName);
            script.onerror = () => this.onError(observer, fileName);

            this.windowRef.document.body.appendChild(script);
            this.loadedFiles.set(fileName, true);
        });
    }

    loadCssFile(fileName: string, isHeadAppend?: boolean, moduleCss?: boolean): Observable<string | null> {
        if (!this.targatedElm) this.targatedElm = this.tagetedElement();

        if (this.loadedFiles.has(fileName) && this.loadedFiles.get(fileName)) {
            return observableOf(null);
        }

        return Observable.create((observer: Observer<string>) => {
            const link = this.windowRef.document.createElement('link');
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.media = 'all';
            link.href = fileName;
            link.onload = () => this.onLoad(observer, fileName);
            link.onerror = () => this.onError(observer, fileName);
            if (this.targatedElm && moduleCss) {
                this.targatedElm.insertAdjacentElement('afterend', link);
            } else if (isHeadAppend) {
                this.windowRef.document.getElementsByTagName('head')[0].appendChild(link);
            } else {
                this.windowRef.document.body.appendChild(link);
            }

            this.loadedFiles.set(fileName, true);
        });
    }

    getAbsoluteManifestPath() {
        if (this.productService.isSingleDomainApp) {
            return `ClientDist/browser/`;
        }
        const brand = this.environment.BRAND === 'bma' ? 'coral' : 'ladbrokes';
        const platform = this.environment.CURRENT_PLATFORM === 'mobile' ? 'mobile' : 'desktop';
        return `ClientDist/${brand}-${platform}-app/browser/`;
    }

    isHashIncluded(path: string): boolean {
        return window.location.hostname.indexOf('bm-tst1') > -1;
        // return Array.from(this.windowRef.document.body.getElementsByTagName('script')).some(
        //     (script) => script.src && script.src.includes('preloader') && !script.src.includes('ClientDist'),
        // );
    }
    tagetedElement(): any {
        return document.querySelector('link[href*="styles."]');
    }
    /**
     * Get svg sprite from cms (S3)
     *
     * @param filePath absolute or relative url to sprite
     */
    getSvgSprite(filePath: string): Observable<string> {
        return this.http.get(filePath).pipe(map((data: { [key: string]: string }) => data.content));
    }

    loadSvgIcons(fileName: string, isLoaded: boolean = true): Observable<string | null> {
        if (this.loadedFiles.has(fileName) && isLoaded) {
            return observableOf(null);
        }

        const httpOptions = {
            responseType: 'text' as const,
        };

        return this.http.get(fileName, httpOptions).pipe(
            map((data: string) => {
                this.loadedFiles.set(fileName, true);
                return data;
            }),
        );
    }

    private onError(observer: Observer<string>, fileName: string): void {
        this.loadedFiles.set(fileName, false);
        observer.error(`Fail to Load ${fileName}`);
        observer.complete();
    }

    private onLoad(observer: Observer<string>, fileName: string): void {
        observer.next(fileName);
        observer.complete();
    }

    async getEntry(entry: string): Promise<string> {
        await Promise.all([this.load(), firstValueFrom(this.loaded.pipe(filter((l) => l)))]);
        return this.data[entry]!;
    }

    private async load(): Promise<void> {
        if (this.loadingStarted) return;

        this.loadingStarted = true;
        if (this.productService.isSingleDomainApp) {
            return;
        }
        const APIOPTIONS: { [name: string]: string } = { prefix: '/en/coralsports' };
        const response$: Observable<{ [key: string]: string }> = this.vanillaApiService.get('manifest/lcgmanifest', {}, APIOPTIONS) as Observable<{
            [key: string]: string;
        }>;
        this.data = (await firstValueFrom<{ [key: string]: string }>(response$)) ?? {};
        this.loaded.next(true);
    }
}
